import React, {useState} from 'react';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

interface FilterConfigOption {
  label: string;
  value: string;
  active?: boolean;
}

interface FieldConfig {
  order: number;
  heading?: string[];
  placeholder?: string[];
  type: string;
  drop_down_options?: FilterConfigOption[];
  validation?: Record<string, any>;
  required?: boolean;
}

interface FilterConfig {
  field_options: FieldConfig;
  operator_type: FieldConfig;
  field_value: FieldConfig;
}

interface FilterBuilderViewProps {
  config: FilterConfig;
  onChange?: (filters: any) => void;
  filters: any;
  setFilters: (filters: any) => void;
}

export default function FilterBuilderView({
  config,
  onChange,
  filters,
  setFilters
}: FilterBuilderViewProps) {
  // normalize helper
  const normalizeOptions = (opts?: any) => {
    if (!opts) return [];
    if (Array.isArray(opts.drop_down_options)) return opts.drop_down_options;
    if (Array.isArray(opts.options)) return opts.options;
    return [];
  };

  // parse options from config
  const fieldOptions = normalizeOptions(config.field_options).filter(
    (o: any) => o.active !== false
  );
  const operatorOptions = normalizeOptions(config.operator_type).filter(
    (o: any) => o.active !== false
  );

  // state
  // handlers
  const addFilter = () => {
    const newFilter = {
      id: crypto.randomUUID(),
      field: '',
      operator: '',
      value: ''
    };
    const newFilters = [...filters, newFilter];
    setFilters(newFilters);
    onChange?.(newFilters);
  };

  const removeFilter = (id: string) => {
    const updated = filters.filter((f: any) => f.id !== id);
    // const finalFilters =
    //   newFilters.length > 0
    //     ? newFilters
    //     : [{id: crypto.randomUUID(), field: '', operator: '', value: ''}];
    setFilters(updated);
    onChange?.(updated);
  };

  const updateFilter = (id: string, key: string, value: string) => {
    const newFilters = filters.map((f: any) =>
      f.id === id ? {...f, [key]: value} : f
    );
    setFilters(newFilters);
    onChange?.(newFilters);
  };

  return (
    <Card variant="outlined" sx={{borderRadius: 3, mt: 3}}>
      <CardContent>
        <Typography variant="h6" sx={{mb: 2}}>
          Filter Builder
        </Typography>

        <Stack spacing={2}>
           {filters.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No filters added. Click “Add Filter” to begin.
              </Typography>
            )}
          {filters.map((filter: any) => {
            // get all selected field values except for this row
            const selectedFields = filters
              .filter((f: any) => f.id !== filter.id && f.field)
              .map((f: any) => f.field);

            const availableFieldOptions = fieldOptions.filter(
              (opt: any) => !selectedFields.includes(opt.value)
            );

            return (
              <Grid
                container
                spacing={2}
                alignItems="center"
                key={filter.id}
                sx={{mb: 1}}>
                {/* ---- FIELD ---- */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>
                      {config.field_options.heading?.[0] || 'Field'}
                    </InputLabel>
                    <Select
                      value={filter.field}
                      label={config.field_options.heading?.[0] || 'Field'}
                      onChange={e => {
                        const newValue = e.target.value;
                        const newFilters = filters.map((f: any) =>
                          f.id === filter.id
                            ? {...f, field: newValue, operator: '', value: ''}
                            : f
                        );
                        setFilters(newFilters);
                        onChange?.(newFilters);
                      }}>
                      <MenuItem value="">
                        <em>
                          {config.field_options.placeholder?.[0] ||
                            'Select Field'}
                        </em>
                      </MenuItem>
                      {availableFieldOptions.map((opt: any) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* ---- OPERATOR ---- */}
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small" disabled={!filter.field}>
                    <InputLabel>
                      {config.operator_type.heading?.[0] || 'Operator'}
                    </InputLabel>
                    <Select
                      value={filter.operator}
                      label={config.operator_type.heading?.[0] || 'Operator'}
                      onChange={e =>
                        updateFilter(filter.id, 'operator', e.target.value)
                      }>
                      <MenuItem value="">
                        <em>
                          {config.operator_type.placeholder?.[0] ||
                            'Select Operator'}
                        </em>
                      </MenuItem>
                      {operatorOptions.map((opt: any) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* ---- VALUE ---- */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={config.field_value.heading?.[0] || 'Value'}
                    placeholder={config.field_value.placeholder?.[0] || ''}
                    type={config.field_value.type || 'text'}
                    value={filter.value}
                    disabled={!filter.operator}
                    onChange={e =>
                      updateFilter(filter.id, 'value', e.target.value)
                    }
                  />
                </Grid>

                {/* ---- REMOVE ---- */}
                <Grid item xs={12} md={1}>
                  <IconButton
                    onClick={() => removeFilter(filter.id)} >
                    <CloseIcon fontSize="small" sx={{ color: 'error.main' }} />
                  </IconButton>
                </Grid>
              </Grid>
            );
          })}
        </Stack>

        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          sx={{mt: 2}}
          onClick={addFilter}>
          Add Filter
        </Button>
      </CardContent>
    </Card>
  );
}
